import { useQuery } from "@tanstack/react-query";
import { CircleNotch, VideoCamera } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { apiGet } from "@/lib/api-error";
import { formatDateTimeBs } from "@/lib/format";

interface CycleDetail {
  sessions: Array<{ id: string; scheduledAt: string; meetLink: string | null; cancelledAt: string | null }>;
  enrollments: Array<{
    id: string;
    paymentStatus: "pending" | "pending_transfer" | "paid" | "refunded" | "failed";
    cancelledAt: string | null;
    client: { id: string; name: string; email: string };
  }>;
}

// Prijave koje drže mjesto; otkazane, istekle i neuspjele terapeut ne vidi.
const SEAT_LABEL: Partial<Record<CycleDetail["enrollments"][number]["paymentStatus"], string>> = {
  paid: "Plaćeno",
  pending_transfer: "Čeka bankovnu uplatu",
  pending: "Plaćanje u toku",
};

/**
 * Terapeutov pregled ciklusa: ko je prijavljen i Meet link svake sesije.
 * Linkove učesnici dobijaju u podsjetnicima, a ovdje ih terapeut ima na jednom mjestu.
 */
export function GroupTherapyParticipantsDialog({
  cycleId,
  cycleName,
  onClose,
}: {
  cycleId: string | null;
  cycleName: string;
  onClose: () => void;
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["therapist", "group-therapy", cycleId],
    queryFn: () =>
      apiGet<CycleDetail>(`/api/group-therapies/${cycleId}/mine`, "Greška pri učitavanju učesnika"),
    enabled: cycleId !== null,
  });

  const participants = (data?.enrollments ?? []).filter(
    (e) => !e.cancelledAt && SEAT_LABEL[e.paymentStatus]
  );
  const sessions = (data?.sessions ?? []).filter((s) => !s.cancelledAt);

  return (
    <Dialog open={cycleId !== null} onClose={onClose}>
      <DialogHeader onClose={onClose}>
        <DialogTitle>{cycleName}</DialogTitle>
        <DialogDescription>
          Učesnici i linkovi za sesije. Link za svaku sesiju dobijate i u podsjetniku dan prije i sat prije.
        </DialogDescription>
      </DialogHeader>
      <DialogBody>
        {isLoading ? (
          <div className="flex h-24 items-center justify-center">
            <CircleNotch className="size-6 animate-spin text-primary" />
          </div>
        ) : isError ? (
          <p className="text-sm text-destructive">Greška pri učitavanju učesnika.</p>
        ) : (
          <div className="space-y-6">
            <section className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground">Učesnici ({participants.length})</h3>
              {participants.length === 0 ? (
                <p className="text-sm text-muted-foreground">Još nema prijava.</p>
              ) : (
                <ul className="divide-y divide-[#ece8df] rounded-lg border border-[#ece8df]">
                  {participants.map((e) => (
                    <li key={e.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                      <div className="min-w-0">
                        <p className="truncate font-medium text-foreground">{e.client.name}</p>
                        <p className="truncate text-xs text-muted-foreground">{e.client.email}</p>
                      </div>
                      <span className="shrink-0 text-xs text-muted-foreground">{SEAT_LABEL[e.paymentStatus]}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground">Termini</h3>
              <ol className="divide-y divide-[#ece8df] rounded-lg border border-[#ece8df]">
                {sessions.map((s, i) => (
                  <li key={s.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                    <span className="text-foreground">
                      <span className="mr-2 text-muted-foreground">{i + 1}.</span>
                      {formatDateTimeBs(s.scheduledAt)}
                    </span>
                    {s.meetLink ? (
                      <a
                        href={s.meetLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex shrink-0 items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
                      >
                        <VideoCamera className="size-3.5" /> Meet
                      </a>
                    ) : (
                      <span className="shrink-0 text-xs text-muted-foreground">Link još nije spreman</span>
                    )}
                  </li>
                ))}
              </ol>
            </section>
          </div>
        )}
      </DialogBody>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Zatvori
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
